
/* ============================== src/hooks/useTaskFileActions.js ============================== */
// src/hooks/useTaskFileActions.js
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DIALOGS, TOASTS } from "../utils/constants";
import {
  useUploadFileMutation,
  useDeleteFileMutation,
  useCreateFolderMutation,
  useDeleteFolderMutation,
} from "../api/queries/files";

/**
 * Побочные действия с файлами и папками задачи.
 * Все операции идут через React Query (API) и обновляют локальный стейт формы.
 */
export function useTaskFileActions({
  values,
  existing,
  setFieldValue,
  toast,
  confirm,
  ownerType = "task", // Для проектов можно передавать "project"
}) {
  const qc = useQueryClient();

  // Отключаем авто-инвалидацию при каждом файле
  const uploadMutation = useUploadFileMutation({ invalidate: false });
  const deleteFileMutation = useDeleteFileMutation();
  const createFolderMutation = useCreateFolderMutation();
  const deleteFolderMutation = useDeleteFolderMutation();

  const targetId = existing?.id || values.id;

  const handleFileUpload = useCallback(
    async (files, folderId = null) => {
      if (!targetId) {
        toast("Сначала сохраните задачу", "warning");
        return;
      }

      for (const file of files) {
        try {
          const uploadedFile = await uploadMutation.mutateAsync({
            file,
            folderId,
            ownerType,
            ownerId: targetId,
          });

          setFieldValue("files", [...(values.files || []), uploadedFile]);
          toast(TOASTS.fileUploaded(file.name), "success");
        } catch (err) {
          toast(err.message || `Ошибка загрузки файла ${file.name}`, "error");
        }
      }

      // Инвалидируем кэш ОДИН раз после завершения цикла загрузки
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["comments"] });
    },
    [
      targetId,
      ownerType,
      uploadMutation,
      values.files,
      setFieldValue,
      toast,
      qc,
    ],
  );

  const handleFileDelete = useCallback(
    async (fileId) => {
      if (!(await confirm(DIALOGS.deleteFile))) return;
      try {
        await deleteFileMutation.mutateAsync(fileId);
        setFieldValue(
          "files",
          (values.files || []).filter((f) => f.id !== fileId),
        );
        toast(TOASTS.fileDeleted, "info");
      } catch (err) {
        toast(err.message || "Не удалось удалить файл", "error");
      }
    },
    [deleteFileMutation, values.files, setFieldValue, toast, confirm],
  );

  const handleCreateFolder = useCallback(
    async (name, parentId = null) => {
      if (!targetId) return;
      try {
        const newFolder = await createFolderMutation.mutateAsync({
          name,
          parentId,
          ownerType,
          ownerId: targetId,
        });
        setFieldValue("folders", [...(values.folders || []), newFolder]);
        toast(TOASTS.folderCreated(name), "success");
      } catch (err) {
        toast(err.message || "Не удалось создать папку", "error");
      }
    },
    [
      targetId,
      ownerType,
      createFolderMutation,
      values.folders,
      setFieldValue,
      toast,
    ],
  );

  const handleDeleteFolder = useCallback(
    async (folderId) => {
      try {
        await deleteFolderMutation.mutateAsync(folderId);
        setFieldValue(
          "folders",
          (values.folders || []).filter((f) => f.id !== folderId),
        );
        toast(TOASTS.folderDeleted, "success");
      } catch (err) {
        toast(err.message || "Не удалось удалить папку", "error");
      }
    },
    [deleteFolderMutation, values.folders, setFieldValue, toast],
  );

  return {
    handleFileUpload,
    handleFileDelete,
    handleCreateFolder,
    handleDeleteFolder,
  };
}
